import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui-kit";
import { Stars } from "@/components/SpotReviews";
import type { EventReview } from "@/components/EventReviews";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { Booking } from "@/types";

export const canRate = (b: Booking) => !!b.event_id && !!b.checked_in && (b.status === "attended" || b.status === "completed");

export const RateButton = ({ b, mine, onClick }: { b: Booking; mine?: EventReview; onClick: () => void }) => {
  const { t } = usePrefs();
  if (!canRate(b)) return null;
  return (
    <button data-testid={`booking-rate-${b.booking_id}`} onClick={onClick} className="h-9 px-4 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 text-xs font-bold inline-flex items-center gap-1.5">
      <Star className={`h-3.5 w-3.5 ${mine ? "fill-amber-500 text-amber-500" : ""}`} />{mine ? t("er.yourRating").replace("{n}", String(mine.rating)) : t("er.rate")}
    </button>
  );
};

export const RateEventDialog = ({ b, mine, onClose, onDone }: { b: Booking; mine?: EventReview; onClose: () => void; onDone: () => void }) => {
  const { t, lang } = usePrefs();
  const [rating, setRating] = useState(mine?.rating || 0);
  const [comment, setComment] = useState(mine?.comment || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!rating) return toast.error(t("rev.pickStars"));
    setBusy(true);
    try {
      await api.post(`/bookings/${b.booking_id}/review`, { rating, comment });
      toast.success(t("er.thanks"));
      onDone();
    } catch (e) { toast.error(errorText(e, t)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent data-testid="rate-event-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>{t("er.dialogTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="font-bold">{lang === "ar" ? b.event_title_ar : b.event_title_en || b.event_title_ar}</p>
          <Stars value={rating} onPick={setRating} size="h-8 w-8" testId="rate-event-stars" />
          <textarea data-testid="rate-event-comment" value={comment} maxLength={400} onChange={(e) => setComment(e.target.value)} rows={3} placeholder={t("er.commentPh")} className="w-full rounded-xl border bg-background p-3 text-sm" />
          <button data-testid="rate-event-submit" disabled={busy} onClick={send} className="h-12 w-full rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("rev.send")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
